import { computed, type ComputedRef } from 'vue'

import { platformActionApi } from '../api/PlatformActionApi'
import { DecideActionFlow, type ActionDecision } from '../flows/DecideActionFlow'
import { LoadActionDetailFlow } from '../flows/LoadActionDetailFlow'
import { RevertActionFlow } from '../flows/RevertActionFlow'
import type {
  PlatformActionDetail,
  PlatformActionProposal,
  PlatformActionRequest,
} from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'
import { isTerminalStatus } from '../service/actionStatus'
import { usePlatformActionStore } from '../stores/platformActionStore'

import { useActionPolling, type UseActionPollingOptions } from './useActionPolling'
import { useErrorMessage } from './useErrorMessage'

import { useI18n } from '@/shared/i18n'
import { useToast, type UiAsyncState } from '@/shared/ui'

interface DetailFlow {
  execute: (
    workspaceId: number,
    actionId: string,
    options?: { silent?: boolean },
  ) => Promise<
    { ok: true; value: PlatformActionDetail } | { ok: false; error: PlatformActionError } | null
  >
}

interface DecisionFlow {
  execute: (
    workspaceId: number,
    actionId: string,
    decision: ActionDecision,
    comment?: string,
  ) => Promise<
    { ok: true; value: PlatformActionRequest } | { ok: false; error: PlatformActionError }
  >
}

interface RevertFlow {
  execute: (
    workspaceId: number,
    actionId: string,
  ) => Promise<
    { ok: true; value: PlatformActionProposal } | { ok: false; error: PlatformActionError }
  >
}

export interface UseActionDetailOptions {
  detailFlow?: DetailFlow
  decisionFlow?: DecisionFlow
  revertFlow?: RevertFlow
  polling?: UseActionPollingOptions
}

export interface UseActionDetail {
  detail: ComputedRef<PlatformActionDetail | null>
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<PlatformActionError | null>
  isOpen: ComputedRef<boolean>
  isPolling: ComputedRef<boolean>
  /** Polling gave up while the request was still running — the drawer offers a manual refresh. */
  pollExhausted: ComputedRef<boolean>
  open: (workspaceId: number, actionId: string) => Promise<void>
  close: () => void
  refresh: (workspaceId: number, actionId: string) => Promise<void>
  decide: (workspaceId: number, actionId: string, decision: ActionDecision) => Promise<boolean>
  /**
   * Propose the inverse of this request. Resolves to the NEW request's id on success — nothing has
   * been undone at that point, it still has to be approved.
   */
  revert: (workspaceId: number, actionId: string) => Promise<string | null>
}

/**
 * The detail drawer: load, live-poll until terminal, and take the two decisions a listed request
 * still allows.
 *
 * Confirm is deliberately NOT here. A confirm spends the `confirm_token`, which the backend only
 * returns from a propose call and binds to that exact payload — so approval lives in the propose
 * modal that holds the token, and a row loaded later can only be rejected or cancelled.
 */
export function useActionDetail(options: UseActionDetailOptions = {}): UseActionDetail {
  const store = usePlatformActionStore()
  const toast = useToast()
  const { t } = useI18n()
  const { errorMessage } = useErrorMessage()

  const detailFlow =
    options.detailFlow ?? new LoadActionDetailFlow({ api: platformActionApi, store })
  const decisionFlow =
    options.decisionFlow ?? new DecideActionFlow({ api: platformActionApi, store })
  const revertFlow = options.revertFlow ?? new RevertActionFlow({ api: platformActionApi })

  const polling = useActionPolling(
    {
      load: async (workspaceId, actionId) => {
        const result = await detailFlow.execute(workspaceId, actionId, { silent: true })
        return result?.ok === true ? result.value.request : null
      },
    },
    options.polling ?? {},
  )

  async function open(workspaceId: number, actionId: string): Promise<void> {
    store.clearDetail()
    const result = await detailFlow.execute(workspaceId, actionId)
    if (result?.ok !== true) return
    if (!isTerminalStatus(result.value.request.status)) polling.start(workspaceId, actionId)
  }

  function close(): void {
    polling.stop()
    store.clearDetail()
  }

  async function refresh(workspaceId: number, actionId: string): Promise<void> {
    polling.stop()
    await open(workspaceId, actionId)
  }

  async function decide(
    workspaceId: number,
    actionId: string,
    decision: ActionDecision,
  ): Promise<boolean> {
    const result = await decisionFlow.execute(workspaceId, actionId, decision)
    if (!result.ok) {
      toast.error(errorMessage(result.error))
      return false
    }
    polling.stop()
    toast.success(t(`platformActions.toast.${decision}`))
    return true
  }

  /**
   * An undo is a PROPOSAL. On success the toast says so explicitly — a "reverted" message here
   * would tell the user a change was rolled back when it is only waiting for approval.
   */
  async function revert(workspaceId: number, actionId: string): Promise<string | null> {
    const result = await revertFlow.execute(workspaceId, actionId)
    if (!result.ok) {
      toast.error(errorMessage(result.error))
      return null
    }
    toast.success(t('platformActions.detail.revertPending'))
    return result.value.request.id
  }

  return {
    detail: computed(() => store.detail),
    state: computed(() => detailState(store.detailStatus, store.detail)),
    error: computed(() => store.detailError),
    isOpen: computed(() => store.detailStatus !== 'idle'),
    isPolling: polling.isPolling,
    pollExhausted: polling.exhausted,
    open,
    close,
    refresh,
    decide,
    revert,
  }
}

function detailState(status: string, detail: PlatformActionDetail | null): UiAsyncState {
  if (status === 'loading') return 'loading'
  if (status === 'error') return 'error'
  return detail ? 'ready' : 'empty'
}
